import merge from 'lodash/merge';
import { createTheme as createMuiTheme, responsiveFontSizes, TypeText } from '@mui/material/styles';
import type { Direction, Theme } from '@mui/material';
import { ThemeOptions } from '@mui/material/styles/createTheme';
import { THEMES } from '../constants';
import { darkShadows, lightShadows } from './shadows';
import { TypeBackground } from '@mui/material/styles/createPalette';

declare module '@mui/material/styles' {
  interface Palette {
    visibilityPrivate: Palette['primary'];
  }
  interface PaletteOptions {
    visibilityPrivate?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    visibilityPrivate: true;
  }
}


interface ThemeConfig {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: string;
}

interface SideNav {
  background: Partial<TypeBackground>
  text: Partial<TypeText>
  selected: string
  active: string
}

interface AppThemOptions extends ThemeOptions {
  sideNav: SideNav
}

const baseOptions: AppThemOptions = {
  sideNav: {
    background: {
      default: '#f4f5f7',
      paper: '#ffffff'
    },
    text: {
      primary: '#172b4d',
      secondary: '#6b778c'
    },
    selected: '#172b4d',
    active: '#6b778c'
  },
  direction: 'ltr',
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none'
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#1744aa'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none'
        }
      }
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.06)'
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.10)'
        }
      }
    }
  },
  typography: {
    button: {
      fontWeight: 600
    },
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem'
    },
    h2: {
      fontWeight: 600,
      fontSize: '3rem'
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.25rem'
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem'
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem'
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem'
    },
    overline: {
      fontWeight: 600
    }
  }
};

const themesOptions: Record<string, AppThemOptions> = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: '#42526e'
            }
          }
        }
      }
    },
    sideNav: {
      background: {
        default: '#030916',
        paper: '#06122d',
      },
      text: {
        primary: '#ffffff',
        secondary: '#919eab'
      },
      active: '#1e5ae3',
      selected: '#f4f5f7',
    },
    palette: {
      action: {
        active: '#6b778c'
      },
      background: {
        default: '#f4f5f7',
        paper: '#ffffff'
      },
      divider: 'rgba(0, 0, 0, 0.06)',
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      mode: 'light',
      primary: {
        contrastText: '#ffffff',
        main: '#1e5ae3',
        dark: '#06122d',
        light: '#8eacf1',
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#1e5ae3'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50',
        light: '#ccfbef',
        dark: '#107569',
      },
      text: {
        primary: '#172b4d',
        secondary: '#6b778c'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      visibilityPrivate: {
        contrastText: '#ffffff',
        main: '#06122d',
      },
      grey: {
        50: '#f9f9f9',
        100: '#f2f3f4',
        200: '#e6e7ea',
        300: '#cfcfd2',
        400: '#a5a9b3',
        500: '#9a9ca1',
        600: '#676b73',
        700: '#353a44',
        800: '#1c212d',
        900: '#030916',
        A100: '#fcfcfc',
        A200: '#f2f3f4',
        A400: '#d2d4d9',
        A700: '#9ba0aa',
      },
    },
    shadows: lightShadows
  },
  [THEMES.DARK]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)'
          }
        }
      }
    },
    sideNav: {
      background: {
        default: '#030916',
        paper: '#06122d',
      },
      text: {
        primary: '#ffffff',
        secondary: '#bdc4cc'
      },
      active: '#1e5ae3',
      selected: '#f4f5f7',
    },
    palette: {
      action: {
        active: '#ffffff',
      },
      background: {
        default: '#1c212d',
        paper: '#030916'
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      mode: 'dark',
      primary: {
        contrastText: '#ffffff',
        main: '#1e5ae3'
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#1e5ae3'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50'
      },
      text: {
        primary: '#ffffff',
        secondary: '#bdc4cc'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      visibilityPrivate: {
        contrastText: '#222b36',
        main: '#ffffff'
      },
      grey: {
        50: '#f9f9f9',
        100: '#f2f3f4',
        200: '#e6e7ea',
        300: '#cfcfd2',
        400: '#a5a9b3',
        500: '#9a9ca1',
        600: '#676b73',
        700: '#353a44',
        800: '#1c212d',
        900: '#030916',
        A100: '#fcfcfc',
        A200: '#f2f3f4',
        A400: '#d2d4d9',
        A700: '#9ba0aa',
      },
    },
    shadows: darkShadows
  }
};

export interface AppTheme extends Theme {
  sideNav?: Partial<SideNav>;
}

export const createTheme = (config: ThemeConfig = {}): Theme => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid.`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createMuiTheme(
    merge(
      {},
      baseOptions,
      themeOptions,
      {
        ...(
          config.roundedCorners && {
            shape: {
              borderRadius: 16
            }
          }
        )
      },
      {
        direction: config.direction
      }
    )
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
